import { useQuery } from '@tanstack/react-query';
import { productsApi, statusesApi, categoriesApi, taxCategoriesApi, priceTypesApi, relationTypesApi } from '~/lib/api/client';
import type { CategoryTreeItem } from '@sleed/bizcommerce-api';
import type { CategoryTreeNode } from '~/components/product/category-picker';

/** Generic id/label lookup row consumed by the filter & mass-edit pickers. */
export interface LookupOption {
	id: number;
	label: string;
}

const LOOKUP_STALE_TIME = 5 * 60_000; // lookups change rarely → 5 min cache

/**
 * Numeric AvailabilityStatus → enum member name. Mirrors the generated
 * `@sleed/bizcommerce-api` `AvailabilityStatus` enum. The loadOptions binder filters
 * `availabilityStatus` with `Enum.TryParse(name)`, so we must send NAMES (e.g. "Exhausted"),
 * not the numeric ids the lookup endpoint returns. Kept as a literal map (rather than a
 * value-import of the numeric enum) to avoid a runtime value dependency on the generated
 * client's dist barrel.
 */
const AVAILABILITY_ENUM_NAMES: Record<number, string> = {
	0: 'OnLineWarehouse',
	1: 'Available',
	2: 'Limited',
	3: 'Exhausted',
	4: 'PreOrder',
	5: 'AfterOrder',
	6: 'Days_1_3',
	7: 'Days_2_5',
	8: 'Days_4_7',
	9: 'Days_7_Over',
	10: 'LastItem',
	11: 'NotAvailable',
	12: 'ComingSoon',
	13: 'OnStock'
};

function availabilityEnumName(value: number): string {
	return AVAILABILITY_ENUM_NAMES[value] ?? '';
}

const lookupKeys = {
	statuses: ['lookups', 'product-statuses'] as const,
	statusBadges: ['lookups', 'status-badges'] as const,
	subTypes: ['lookups', 'product-subtypes'] as const,
	availability: ['lookups', 'product-availability'] as const,
	// Separate key: same endpoint, but cached as {id,label} rather than {name,label}.
	availabilityOptions: ['lookups', 'product-availability-options'] as const,
	manufacturers: ['lookups', 'product-manufacturers'] as const,
	categoryTree: ['lookups', 'category-tree'] as const,
	taxCategories: ['lookups', 'tax-categories'] as const,
	priceTypes: ['lookups', 'price-types'] as const,
	relationTypes: ['lookups', 'relation-types'] as const
};

/** A storefront-badge status row (id + name + optional badge colour + sort) for the States tab. */
export interface StatusBadgeOption {
	id: number;
	label: string;
	colorHex: string | null;
	sortIndex: number;
}

/**
 * Full storefront-badge status catalogue (StatusesApi.getAll → StatusTokenList.items) for the
 * States tab. Unlike `useProductStatuses` (id/label only for the filter), this carries the badge
 * colour (`chex`) and sort index so the toggle grid can render swatches in the configured order.
 */
export function useStatusBadges() {
	return useQuery<StatusBadgeOption[]>({
		queryKey: lookupKeys.statusBadges,
		staleTime: LOOKUP_STALE_TIME,
		queryFn: async () => {
			const res = await statusesApi.getAll();
			return (res.data.items ?? [])
				.filter(s => s.id != null)
				.map(s => ({ id: s.id as number, label: s.nm ?? '', colorHex: s.chex ?? null, sortIndex: s.si ?? 0 }))
				.sort((a, b) => a.sortIndex - b.sortIndex);
		}
	});
}

/**
 * Relation types for the Relations tab grouping (RelationTypesApi.getAll → RelationTypeTokenList.items).
 * Each relation group (Accessory / Combined with / Related) is keyed by RelationTypeId.
 */
export function useRelationTypes() {
	return useQuery<LookupOption[]>({
		queryKey: lookupKeys.relationTypes,
		staleTime: LOOKUP_STALE_TIME,
		queryFn: async () => {
			const res = await relationTypesApi.getAll();
			return (res.data.items ?? []).filter(rt => rt.id != null).map(rt => ({ id: rt.id as number, label: rt.nm ?? '' }));
		}
	});
}

/** Product statuses (StatusesApi.getAll → StatusTokenList.items). */
export function useProductStatuses() {
	return useQuery<LookupOption[]>({
		queryKey: lookupKeys.statuses,
		staleTime: LOOKUP_STALE_TIME,
		queryFn: async () => {
			const res = await statusesApi.getAll();
			return (res.data.items ?? []).filter(s => s.id != null).map(s => ({ id: s.id as number, label: s.nm ?? '' }));
		}
	});
}

/** Product sub-types (ProductsApi.getAllSubTypes → SubTypeTokenList.items). */
export function useProductSubTypes() {
	return useQuery<LookupOption[]>({
		queryKey: lookupKeys.subTypes,
		staleTime: LOOKUP_STALE_TIME,
		queryFn: async () => {
			const res = await productsApi.getAllSubTypes();
			return (res.data.items ?? []).filter(s => s.id != null).map(s => ({ id: s.id as number, label: s.nm ?? '' }));
		}
	});
}

/**
 * Availability-status filter option. `name` is the enum member name (e.g. "Exhausted") —
 * the token the loadOptions binder parses via `Enum.TryParse(name)`. `label` is the
 * localized display text returned by the API.
 */
export interface AvailabilityOptionRow {
	name: string;
	label: string;
}

/**
 * Availability statuses for the filter (ProductsApi.getAvailabilityStatuses → EnumTokenList).
 * The EnumToken `id` is the numeric enum value; we resolve the enum member NAME from the
 * generated `AvailabilityStatus` enum because the binder filters on names, not numbers.
 */
export function useProductAvailabilityStatuses() {
	return useQuery<AvailabilityOptionRow[]>({
		queryKey: lookupKeys.availability,
		staleTime: LOOKUP_STALE_TIME,
		queryFn: async () => {
			const res = await productsApi.getAvailabilityStatuses();
			return (res.data.items ?? [])
				.filter(e => e.id != null && e.nm)
				.map(e => ({ name: availabilityEnumName(e.id as number), label: e.nm as string }))
				.filter(o => o.name !== '');
		}
	});
}

/**
 * Availability statuses keyed by NUMERIC id (`AvailabilityStatus` enum value) — the shape the
 * product editor form holds in `availabilityStatus`. Reuses the same cache key as the
 * filter hook's endpoint but maps to {id,label}; the editor binds the numeric id directly.
 */
export function useProductAvailabilityOptions() {
	return useQuery<LookupOption[]>({
		queryKey: lookupKeys.availabilityOptions,
		staleTime: LOOKUP_STALE_TIME,
		queryFn: async () => {
			const res = await productsApi.getAvailabilityStatuses();
			return (res.data.items ?? []).filter(e => e.id != null && e.nm).map(e => ({ id: e.id as number, label: e.nm as string }));
		},
		// Re-shape the shared availability response into {id,label}; the filter hook also caches it.
		select: rows => rows.map(r => ({ id: r.id, label: r.label }))
	});
}

/** Manufacturers for the Basic-tab "Manufacturer" select (CategoriesApi.getAllFeaturedManufacturers). */
export function useProductManufacturers() {
	return useQuery<LookupOption[]>({
		queryKey: lookupKeys.manufacturers,
		staleTime: LOOKUP_STALE_TIME,
		queryFn: async () => {
			const res = await categoriesApi.getAllFeaturedManufacturers();
			return (res.data.items ?? []).filter(m => m.id != null).map(m => ({ id: m.id as number, label: m.nm ?? '' }));
		}
	});
}

/** Map the generated CategoryTreeItem (nested) onto the CategoryPicker's CategoryTreeNode shape. */
function toTreeNode(item: CategoryTreeItem): CategoryTreeNode {
	return {
		id: item.id ?? 0,
		name: item.name ?? '',
		code: item.code,
		children: item.children && item.children.length > 0 ? item.children.map(toTreeNode) : undefined
	};
}

/**
 * Depth requested from CategoriesApi.getCategoriesTree. The endpoint only populates `Children`
 * when `treeLevel > 1` (default 1 = root only), so we ask for the full tree — the CategoryPicker
 * renders it client-side and its search / "expand all" need every node present. The server walks
 * an already-loaded in-memory list and stops recursing at leaves, so an oversized depth is safe.
 */
const CATEGORY_TREE_FULL_DEPTH = 100;

/** Live category tree for the Categories tab (CategoriesApi.getCategoriesTree → nested items). */
export function useCategoryTree() {
	return useQuery<CategoryTreeNode[]>({
		queryKey: lookupKeys.categoryTree,
		staleTime: LOOKUP_STALE_TIME,
		queryFn: async () => {
			const res = await categoriesApi.getCategoriesTree(undefined, undefined, CATEGORY_TREE_FULL_DEPTH);
			return (res.data.items ?? []).map(toTreeNode);
		}
	});
}

/**
 * VAT/tax categories for the Prices-tab "VAT category" select
 * (TaxCategoriesApi.getAll → TaxCategoryTokenList.items). Label folds in the rate when present
 * (e.g. "ΦΠΑ 6%"). Cached 5 min like the other lookups.
 */
export function useTaxCategories() {
	return useQuery<LookupOption[]>({
		queryKey: lookupKeys.taxCategories,
		staleTime: LOOKUP_STALE_TIME,
		queryFn: async () => {
			const res = await taxCategoriesApi.getAll();
			return (res.data.items ?? [])
				.filter(c => c.id != null)
				.map(c => ({
					id: c.id as number,
					label: c.rt != null ? `${c.nm} (${c.rt}%)` : c.nm
				}));
		}
	});
}

/** VAT-inclusion + label resolved from the price-type schema, keyed by PriceTypeId. */
export interface PriceTypeMeta {
	/** True when the price type stores VAT-inclusive prices (PriceTaxMode.AfterTaxes = 1). */
	vatIncluded: boolean;
	label: string;
	isBase: boolean;
}

// PriceTaxMode.AfterTaxes — kept as a literal (matches the file's policy of not value-importing
// generated enums from the dist barrel). 0 = BeforeTaxes (Excl. VAT), 1 = AfterTaxes (Incl. VAT).
const PRICE_TAX_MODE_AFTER_TAXES = 1;

/**
 * Price-type schema map (PriceTypesApi.getAll → PriceTypeTokenList.items) keyed by PriceTypeId.
 * The Prices tab joins this against each `prclst` row to fill the READ-ONLY VAT-inclusion pill
 * and DEFAULT badge that the price row itself does not carry.
 */
export function usePriceTypeMeta() {
	return useQuery<Map<number, PriceTypeMeta>>({
		queryKey: lookupKeys.priceTypes,
		staleTime: LOOKUP_STALE_TIME,
		queryFn: async () => {
			const res = await priceTypesApi.getAll();
			const map = new Map<number, PriceTypeMeta>();
			for (const pt of res.data.items ?? []) {
				if (pt.id == null) continue;
				map.set(pt.id, {
					vatIncluded: pt.taxMode === PRICE_TAX_MODE_AFTER_TAXES,
					label: pt.nm,
					isBase: pt.base ?? false
				});
			}
			return map;
		}
	});
}
